/**
 * analytics.ts — fire-and-forget product analytics.
 *
 * Events POST to /api/analytics/events, where the backend validates them
 * against an allowlisted schema and writes a structured log line. Each
 * event is also dispatched on window as `swipehome:analytics` so a client
 * analytics SDK can subscribe later without touching call sites.
 *
 * Tracking never throws and never blocks the UI: failures are swallowed,
 * and a 401 here does NOT trigger the api() logout redirect.
 */

import { API } from './api.js';
import { state } from './state.js';

export type ProviderCtaPlacement = 'deal_analysis' | 'financing' | 'condition_report' | 'portfolio_renewal';
export type ProviderCtaProvider = 'insurance' | 'lender' | 'inspector';

export interface AnalyticsEvents {
  provider_cta_click: {
    placement: ProviderCtaPlacement;
    provider: ProviderCtaProvider;
    listing_id?: string;
    property_id?: string;
  };
}

export function trackEvent<E extends keyof AnalyticsEvents>(event: E, properties: AnalyticsEvents[E]): void {
  try {
    window.dispatchEvent(new CustomEvent('swipehome:analytics', { detail: { event, properties } }));
  } catch {
    /* CustomEvent unsupported — ignore */
  }
  if (!state.token) return;
  // keepalive lets the request finish even if the click navigates away.
  fetch(`${API}/analytics/events`, {
    method: 'POST',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${state.token}`,
    },
    body: JSON.stringify({ event, properties }),
  }).catch(() => {
    /* analytics is best-effort */
  });
}
